import type {
  CertificationCatalogue,
  CertificationCatalogueEntry,
  CertificationKeyValue,
  ResourceTypeValue,
} from '@my/directory-api';

/**
 * The catalogue JSON uses lower_snake_case keys (`"adr"`, `"driver_card"`)
 * while the BE enum on the wire is PascalCase (`"Adr"`, `"DriverCard"`).
 * These helpers bridge the two — keep them in lock-step with the BE
 * `CertificationKey` enum + JSON catalogue.
 */
export function certKeyToSnake(key: CertificationKeyValue): string {
  // PascalCase → lower_snake_case. "DriverCard" → "driver_card".
  return key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

export function snakeToCertKey(snake: string): CertificationKeyValue {
  // "driver_card" → "DriverCard". Caller guarantees the input is a known
  // catalogue key (via filter / has-check first); a malformed input
  // would still convert but produce a value the BE would reject.
  return snake
    .split('_')
    .map((part) => {
      // `noUncheckedIndexedAccess` makes `part[0]` possibly undefined for
      // empty segments (e.g. consecutive underscores); guard so the
      // checker is happy.
      const first = part[0];
      return first === undefined ? part : first.toUpperCase() + part.slice(1);
    })
    .join('') as CertificationKeyValue;
}

/**
 * Filter the catalogue to entries that apply to a given resource type
 * AND (optionally) a given country. Country filtering is a future
 * concern — resources don't carry a country yet, so the FE picker
 * always passes `null` and shows every entry whose `appliesTo` matches.
 */
export function filterCatalogueByType(
  catalogue: CertificationCatalogue | undefined,
  resourceType: ResourceTypeValue,
  countryIsoCode: string | null = null,
): CertificationCatalogueEntry[] {
  if (!catalogue) return [];
  const typeKey = resourceType.toLowerCase();
  const country = countryIsoCode?.toUpperCase() ?? null;
  return catalogue.certifications.filter((c) => {
    if (!c.appliesTo.includes(typeKey)) return false;
    if (country && !c.countries.includes('*') && !c.countries.includes(country)) return false;
    return true;
  });
}

/** Pretty filesize ("240 KB", "1.4 MB"). Three sig figs is enough. */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb >= 100 ? 0 : 1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb >= 100 ? 0 : 1)} MB`;
}
